import { returnPartyKind, returnPartyName } from "../returnParty";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { PlusCircle, Eye } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import PrimaryCell from "../../../../components/common/PrimaryCell";
import DateRangeFilter, { useDateRangeFilter } from "../../components/list/DateRangeFilter";
import ListExportToolbar from "../../components/list/ListExportToolbar";
import { makeGrandTotalSummary } from "../../components/list/GrandTotalSummary";
import { sumField } from "../../components/list/listExportUtils";
import { useListStockReturnsQuery } from "../../stockApi";
import { useCanCreateReturn } from "../../useStockPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const RETURN_TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "STOCK_OUT_BASED", label: "Stock-Out Based" },
  { value: "DIRECT_SUPPLIER", label: "Direct to Supplier" },
];

const StockReturnList = () => {
  const canCreate = useCanCreateReturn();
  const navigate = useNavigate();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [returnType, setReturnType] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [supplierName, setSupplierName] = useState("");
  const { dateFrom, setDateFrom, dateTo, setDateTo } = useDateRangeFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading } = useListStockReturnsQuery({
    branchId: selectedBranchId ?? undefined,
    returnType: returnType || undefined,
    search: search || undefined,
    includeDeleted,
    // 5000, not 100 (2026-10-09): only the newest 100 ever loaded, so older documents
    // vanished and the client-side date filter could not reach them. Volumes are small
    // (remote: 536 stock-outs, 177 LPOs) - same cap as Stock Balances.
    pageSize: 5000,
  });

  // No employee field exists on this record even for a STOCK_OUT_BASED
  // return (only stockOutNo/supplierName) - filtering by supplier here;
  // an "issued to" employee filter would need the API to add that field.
  const filteredItems = (data?.items || []).filter((r) => {
    if (r.returnDate) {
      const d = dayjs(r.returnDate).format("YYYY-MM-DD");
      if ((dateFrom && d < dateFrom) || (dateTo && d > dateTo)) return false;
    }
    if (supplierName.trim() && !(returnPartyName(r) || "").toLowerCase().includes(supplierName.trim().toLowerCase())) {
      return false;
    }
    return true;
  });

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const exportColumns = [
    { header: "Return No", value: (r) => r.returnNo },
    { header: "Branch", value: (r) => r.branchCode },
    { header: "Type", value: (r) => (r.returnType === "STOCK_OUT_BASED" ? "Stock-Out Based" : "Direct to Supplier") },
    { header: "Party", value: (r) => returnPartyName(r) || "-" },
    { header: "Stock Out", value: (r) => r.stockOutNo || "-" },
    { header: "Date", value: (r) => (r.returnDate ? new Date(r.returnDate).toLocaleDateString() : "-") },
    { header: "Total Amount", value: (r) => Number(r.totalAmount || 0).toFixed(2), align: "right" },
    { header: "Status", value: (r) => (r.isDeleted ? "Deleted" : "Posted") },
  ];
  const exportTotals = { "Total Amount": sumField(filteredItems, "totalAmount").toFixed(2) };

  const columns = [
    {
      // The party leads (2026-09-12): the supplier, or for a stock-out based return whoever
      // the Stock Out went to (Batch206, 2026-10-09). The Return number is the link, with
      // the Stock Out number beside it.
      title: "Return",
      dataIndex: "returnNo",
      render: (value, record) => (
        <PrimaryCell
          primary={returnPartyName(record)}
          secondary={value}
          to={paths.stockReturnDetail(record.recordGuid)}
          extra={[returnPartyKind(record), record.stockOutNo].filter(Boolean).join(" · ") || undefined}
        />
      ),
    },
    { title: "Branch", dataIndex: "branchCode" },
    { title: "Type", dataIndex: "returnType", render: (v) => (v === "STOCK_OUT_BASED" ? "Stock-Out Based" : "Direct to Supplier") },
    { title: "Stock-Out Ref", dataIndex: "stockOutNo", render: (v) => v || "-" },
    { title: "Date", dataIndex: "returnDate", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
    { title: "Total Amount", dataIndex: "totalAmount", render: (v) => Number(v || 0).toFixed(2) },
    {
      title: "Status",
      dataIndex: "isDeleted",
      render: (v) => (v ? <span className="badge badge-linedanger">Deleted</span> : <span className="badge badge-linesuccess">Posted</span>),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <button type="button" className="btn btn-link p-2" title="View" onClick={() => navigate(paths.stockReturnDetail(record.recordGuid))}>
          <Eye size={16} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Stock Returns</h4>
              <h6>Returned to stock from an issuance, or returned direct to a supplier</h6>
            </div>
          </div>
          {canCreate && (
            <div className="page-btn">
              <button type="button" className="btn btn-added" onClick={() => navigate(paths.stockReturnNew)}>
                <PlusCircle className="me-2" size={16} />
                New Return
              </button>
            </div>
          )}
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search Return No"
                    className="form-control form-control-sm formsearch"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <DateRangeFilter dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
                <select className="form-control form-control-sm" style={{ width: 150 }} value={returnType} onChange={(e) => setReturnType(e.target.value)}>
                  {RETURN_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Customer / employee / supplier"
                  style={{ width: 150 }}
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                />
              </div>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="return-include-deleted"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="return-include-deleted">Show deleted</label>
              </div>
              <ListExportToolbar
                title="Stock Returns"
                subtitleLines={[`Branch: ${branchCode || "All"}`, `Date: ${dateFrom} to ${dateTo}`]}
                branchCode={branchCode}
                columns={exportColumns}
                rows={filteredItems}
                totals={exportTotals}
                fileName="StockReturns"
                groupBy={(r) => (r.returnType === "STOCK_OUT_BASED" ? "Stock-Out Based" : "Direct to Supplier")}
              />
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={filteredItems}
                rowKey="recordGuid"
                loading={isLoading}
                summary={makeGrandTotalSummary(columns, filteredItems, ["totalAmount"], "returnNo")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockReturnList;
